// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server runs on a different port. Use the
  // current page's hostname (not "localhost") so the URL resolves
  // correctly when the dev server is reached from another device on the
  // LAN — e.g. testing on a phone via http://<mac-ip>:3000.
  if (useDevApiServer) {
    const host =
      typeof window !== 'undefined' && window.location?.hostname
        ? window.location.hostname
        : 'localhost';
    return `http://${host}:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  if (marketplaceRootURL) {
    return marketplaceRootURL.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  // Server-side rendering has no window. Reading window.location here used to
  // throw a ReferenceError, and because the throw happened synchronously inside
  // request(), it escaped the .catch() of every duck that calls these helpers.
  // One such call sits before the listing search in the search page's loadData,
  // so the whole SSR data load failed and every /annonser, /dack and /falgar
  // page was rendered with zero listings — which the schema builder turns into
  // "noindex". Fall back to the configured root URL instead.
  const rootURLFromEnv = process.env.REACT_APP_MARKETPLACE_ROOT_URL;
  return rootURLFromEnv ? rootURLFromEnv.replace(/\/$/, '') : '';
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const checkout = body => {
  return post('/api/checkout', body);
};

export const postStripeCheckout = body => {
  return post('/api/stripe-checkout', body)
}

export const postCheckStripePayment = body => {
  return post('/api/stripe-validate', body)
}

export const payOffPlatform = body => {
  return post('/api/off-platform-payment', body)
}

export const postStripeDashboard = () => {
  return post('/api/stripe-dashboard', {})
}

// Onboarding status of the signed-in seller's connected Stripe account. Used by
// the payouts page to pick between the embedded onboarding and account
// management components. Resolves with `{ account: null }` when the seller has
// no connected account yet.
export const postStripeAccountStatus = () => {
  return post('/api/stripe-account-status', {});
};

// Whether a seller can take payment for an order (P25009-452 BR-1). The
// Marketplace API hides `stripeConnected` on other users, so the listing page
// asks the server, which answers with the same predicate the privileged
// initiate endpoints enforce. Resolves `{ canTakePayment, useOwnPayment }`.
export const postSellerPaymentStatus = body => {
  return post('/api/seller-payment-status', body);
};

export const deleteListing = body => {
  return post('/api/delete-listing', body);
};

export const updateListingGeneral = body => {
  return post('/api/update-listing', body);
};
export const updateTotalLikes = body => {
  return post('/api/update-total-likes', body);
};
export const sendAutoMessage = body => {
  return post('/api/autoMessages/send-auto-message', body);
};
export const getAutoMessages = body => {
  return post('/api/autoMessages/get-auto-messages', body);
};
export const updateUserProfileGeneral = body => {
  return post('/api/update-user-profile', body);
};

export const hideListingEndpoint = body => {
  return post('/api/hide-listing', body);
};

export const getConnectedUserStatus = body => {
  return post('/api/subusers/get-connected-user-status', body);
};

export const createConnectedUser = body => {
  return post('/api/subusers/create-connected-user', body);
};

export const getSubUsers = body => {
  return post('/api/subusers/get-sub-users', body);
};

export const addSubUser = body => {
  return post('/api/subusers/add-sub-user', body);
};

export const deleteSubUser = body => {
  return post('/api/subusers/delete-sub-user', body);
};

export const getInfoFromToken = body => {
  return post('/api/subusersNoAuth/get-info-from-token', body);
};

export const resetPassword = body => {
  return post('/api/subusersNoAuth/reset-password', body);
};

export const getAuthInfo = body => {
  return post('/api/subusersNoAuth/get-auth-info', body);
};

export const getSubUserInfo = body => {
  return post('/api/subusersNoAuth/get-sub-user-info', body);
};

export const sendRecoveryEmail = body => {
  return post('/api/subusersNoAuth/send-recovery-email', body);
};

export const setTempPassword = body => {
  return post('/api/subusersNoAuth/set-temp-password', body);
};

export const setPassword = body => {
  return post('/api/subusersNoAuth/set-password', body);
};

export const postCreateReserveListing = body => {
  return post('/api/reservations/create', body);
}

export const postDeleteReserveListing = body => {
  return post('/api/reservations/delete', body);
}

export const postGetAllReserveListing = body => {
  return post('/api/reservations/get-all', body);
}
export const postcomment = body => {
  return post('/api/reservations/comment', body);
}

// Warehouses
export const postCreateWarehouse = body => {
  return post('/api/warehouses/create', body);
};

export const postUpdateWarehouse = body => {
  return post('/api/warehouses/update', body);
};

export const postDeleteWarehouse = body => {
  return post('/api/warehouses/delete', body);
};
export const postControlWarehouse = body => {
  return post('/api/warehouses/controller', body);
};

export const postGetAllWarehouses = body => {
  return post('/api/warehouses/get', body);
};

// Storage module ("Förvaring") — tire hotel + trade-in. The listing stays the
// single source of truth; these endpoints keep a small Mongo search index in
// sync and record the audit trail. See server/api/storage.
export const postStorageSearch = body => {
  return post('/api/storage/search', body);
};

export const postStorageSync = body => {
  return post('/api/storage/sync', body);
};

export const postStorageLogAction = body => {
  return post('/api/storage/log-action', body);
};

// The storage card's history timeline (P25009-451): the audit actions for one
// stored set. Ownership is checked server-side against the caller's session.
export const postStorageHistory = body => {
  return post('/api/storage/history', body);
};
// The customer's storage receipt PDF (P25009-485 BR-12), opened in a new tab
// (GET, same-origin cookies). The server checks ownership against the session.
export const storageReceiptUrl = listingId =>
  `${apiBaseUrl()}/api/storage/receipt/${encodeURIComponent(listingId)}`;
// The Förvaringsnr for a new storage record (P25009-485 BR-10): "F-" + reg,
// "-2" when that reg already has an active record. { listingId, regPlate } ->
// { storageNumber }.
export const postStorageNumber = body => {
  return post('/api/storage/number', body);
};



export const createAccessRequest = body => {
  return post('/api/AccessRequests/create-access-request', body);
};

export const getOwnAccessRequests = body => {
  return post('/api/AccessRequests/get-own-access-requests', body);
};

export const getAccessRequests = body => {
  return post('/api/AccessRequests/get-access-requests', body);
};

export const updateAccessRequest = body => {
  return post('/api/AccessRequests/update-access-request', body);
};

export const getConnectedUsers = body => {
  return post('/api/AccessRequests/get-connected-users', body);
};

export const getConnectedUsersListingsCount = body => {
  return post('/api/AccessRequests/get-connected-users-listings-count', body);
};
export const fetchUserListings = body => {
  return post('/api/AccessRequests/fetch-user-listings', body);
};

export const deleteAccessRequest = body => {
  return post('/api/AccessRequests/delete-access-request', body);
};

export const createSaveQuery = body => {
  return post('/api/savedQueries/create-save-query', body);
};

export const getSavedQueries = body => {
  return post('/api/savedQueries/get-saved-queries', body);
};

export const deleteSavedQuery = body => {
  return post('/api/savedQueries/delete-saved-query', body);
};

export const updateSavedQuery = body => {
  return post('/api/savedQueries/update-saved-query', body);
};

// Bevakningar (saved searches) — P25009-371. Note these are NOT the
// savedQueries helpers above: those are the "Dina bilar" saved cars.
export const createSavedSearch = body => {
  return post('/api/saved-searches', body);
};

export const getSavedSearches = () => {
  return request('/api/saved-searches', { method: methods.GET });
};

export const updateSavedSearch = (id, body) => {
  return request(`/api/saved-searches/${id}`, { method: methods.PATCH, body });
};

export const deleteSavedSearch = id => {
  return request(`/api/saved-searches/${id}`, { method: methods.DELETE });
};

export const markSavedSearchesOpened = (body = {}) => {
  return post('/api/saved-searches/opened', body);
};

export const getSavedSearchNewCounts = () => {
  return request('/api/saved-searches/new-counts', { method: methods.GET });
};

export const getLicensePlateInfo = body => {
  return post('/api/get-license-plate-info', body);
};

export const getWheelSize = body => {
  return post('/api/get-wheel-size', body);
};
export const updateTxMetadata = body => {
  return post('/api/update-tx-metadata', body);
};
export const updateTxSearchData = body => {
  return post('/api/update-tx-search-data', body);
};


export const generateDescription = body => {
  return post('/api/generate-description', body);
};

export const aiTryOn = body => {
  return post('/api/ai-try-on', body);
};

export const analyzeListingImages = body => {
  return post('/api/analyze-listing-images', body);
};

export const searchCarModel = body => {
  return post('/api/search-car-model', body);
};

export const suggestPrice = body => {
  return post('/api/suggest-price', body);
};

export const recordSearchEvent = body => {
  return post('/api/searchKeywords/recordEvent', body);
};

export const markZeroResult = body => {
  return post('/api/searchKeywords/markZeroResult', body);
};

export const listCarDemand = body => {
  return post('/api/searchKeywords/listDemand', body);
};

/* Enterprise commission (P25009-422). Admin only — the server refuses anyone
 * without the admin flag, so these are safe to import anywhere.
 *
 * Leakage: per dealer, how much of what we sourced their stock movements
 * actually accounted for. The number that makes under-reporting visible. */
export const getCommissionLeakage = body => {
  return post('/api/commission-inventory/leakage', body || {});
};

/* The escalation ladder (P25009-423). Cases are listed, started from an overdue
 * Fortnox invoice, and closed when payment registers — all admin only. */
export const getEnforcementCases = body => {
  return post('/api/commission-inventory/enforcement', body || {});
};

export const openOverdueInvoiceCase = body => {
  return post('/api/commission-inventory/enforcement/overdue', body);
};

export const resolveEnforcementCase = ({ id, ...body }) => {
  return post(`/api/commission-inventory/enforcement/${id}/resolve`, body);
};

/* Every supplier feed with its commission settings, and the one call that
 * changes them. Separate from the supplier-feed admin API on purpose: this
 * cannot touch a feed's URL, credentials or column mapping. */
export const getCommissionFeeds = body => {
  return post('/api/commission-inventory/feeds', body || {});
};

export const saveCommissionFeedSettings = ({ id, ...body }) => {
  return post(`/api/commission-inventory/feeds/${id}`, body);
};

/* The other direction: sales we sent a dealer that their stock never accounted
 * for, and that are now too old to still match (P25009-422 BR-3). */
export const getUnmatchedSourcedSales = body => {
  return post('/api/commission-inventory/unmatched', body || {});
};

/* Suspected off-platform sales: a listed article's stock dropped with no Buy
 * Now of ours behind it. */
export const getCommissionReviewQueue = body => {
  return post('/api/commission-inventory/review', body || {});
};

/* What a person decided about one of them — invoice it under the contract, or
 * dismiss it. Never a silent zero (BR-5). */
export const decideCommissionReview = ({ id, ...body }) => {
  return post(`/api/commission-inventory/review/${id}`, body);
};

/* "Passar även" — which cars a listing fits, derived from its fitment facts
 * rather than from a model tag the seller typed (P25009-398). */
export const fetchListingFitment = body => {
  return post('/api/listing-fitment', body);
};

/* Admin CSV export of the whole car-model demand table (all pages, honouring
 * an optional `search`). The endpoint answers with text/csv, so `request`
 * falls through to res.text() and this resolves to the CSV string. */
export const exportCarDemandCsv = body => {
  return post('/api/searchKeywords/exportDemand', body || {});
};

/* Admin: re-check every failed dimension against live stock, so "we have none"
 * can be told apart from "the search failed" (P25009-401). One listings query
 * per distinct dimension, so this takes a while on a large table — the caller
 * should show progress. */
export const refreshCarDemandGaps = body => {
  return post('/api/searchKeywords/refreshGaps', body || {});
};

export const handleBuyerNotification = body => {
  return post('/api/notify-buyer', body);
};
export const postQueryListings = body => {
  return post('/api/query-listings', body);
}
export const getHighlightedListings = body => {
  return post('/api/get-highlighted-listings', body);
}
export const getBoostedListings = body => {
  return post('/api/get-boosted-listings', body);
}

export const capturePayment = body => {
  return post('/api/capture-payment', body);
}

// Seller cancels an order they cannot fulfil (P25009-458 BR-3). The server
// verifies the caller is the transaction's provider and fires the operator
// cancel transition (full refund + stock return) on their behalf.
export const sellerCancelOrder = body => {
  return post('/api/seller-cancel-order', body);
};

// Seller books freight for a shipping order (P25009-450). The server verifies
// the caller is the transaction's provider and stamps metadata.freight
// (tracking number, weight, package, carrier cost); the caller then fires the
// ordinary mark-delivered transition so the stamped snapshot rides the
// transition event into the buyer's order-shipped mail.
export const stampFreightBooking = body => {
  return post('/api/freight-booking', body);
};

export const retrieveSubscriptionId = body => {
  return post('/api/retrieve-subscription-id', body);
};

export const retrieveUserSubscriptionInfo = body => {
  return post('/api/retrieve-user-subscription-into', body);
};

// User subscription
export const initiateRecurringPayment = body => {
  return post('/api/user-stripe/initiate-recurring-payment', body);
};

export const cancelSubscription = body => {
  return post('/api/user-stripe/cancel-recurring-payment', body);
};

export const verifiedSubscription = body => {
  return post('/api/verified-subscription', body);
}

// Listing payment
export const initiateListingPayment = body => {
  return post('/api/stripe/initiate-recurring-payment', body);
};
export const initiateOneTimeListingPayment = body => {
  return post('/api/stripe/initiate-one-payment', body);
};

// Listing payment — applied by the server after Stripe confirms it. Pass the
// listingId plus the Stripe reference the flow has: { sessionId } (hosted
// return), { paymentIntentId } (embedded one-time) or { subscriptionId }.
export const confirmListingPayment = body => {
  return post('/api/listing-payments/confirm', body);
};

// The 30-day window of a free listing; the server decides who gets one.
export const startFreeListingWindow = body => {
  return post('/api/listing-payments/free-window', body);
};

export const cancelListingSubscription = body => {
  return post('/api/stripe/cancel-recurring-payment', body);
};

export const listingBoostedPayment = body => {
  return post('/api/stripe/listing-boosted-payment', body);
};

/* The seller's plan is granted by the server. `request` covers the free plans,
 * Premium by invoice and the Enterprise request ({ tierName, paymentMethod? });
 * `activate` grants Premium after an embedded card payment ({ subscriptionId }). */
export const requestSubscriptionPlan = body => {
  return post('/api/subscription/request', body);
};

export const activateSubscriptionPlan = body => {
  return post('/api/subscription/activate', body);
};

export const getUserData = body => {
  return post('/api/get-user-data', body);
};

// Part A — Google Reviews badge. Sends only the profile user id; the server
// resolves the admin-set place id and returns the live rating (or a plain
// reviews link on failure).
export const getGoogleReviews = body => {
  return post('/api/google-reviews', body);
};

// P25009-486 BR-3 — the median price for 4 of a dimension: { publicData } ->
// { median: { medianSubunits, sampleSize, scope } | null }.
// P25009-486 BR-7 — { userId } -> the seller's Google place for the pickup
// card: { hasPlace, name, address, location, mapsUrl, rating, reviewCount,
// openNow, weekdayDescriptions } or { hasPlace: false }.
export const getSellerPlace = body => {
  return post('/api/seller-place', body);
};

export const getPriceCheck = body => {
  return post('/api/price-check', body);
};

export const bulkListingUpload = body => {
  return post('/api/bulk-listing-upload', body);
};

export const getBulkListingUploadJobs = body => {
  return post('/api/bulk-listing-upload/jobs', body);
};

export const getBulkListingUploadJob = body => {
  return post('/api/bulk-listing-upload/job', body);
};

/* Undo an import: closes the listings that job created. The Integration API has
 * no listing delete, so "removed" means closed — out of search, not buyable,
 * and reopened if the same file is uploaded again. */
export const rollbackBulkListingUpload = body => {
  return post('/api/bulk-listing-upload/rollback', body);
};

/* P25009-449 — the review-before-import step. A fresh upload is analyzed and
 * parked in `awaiting_review`; confirm is the seller's "publish", discard
 * walks away, and title-preview renders the seller's title pattern on three
 * real rows from their own file before anything exists. */
export const confirmBulkListingUpload = body => {
  return post('/api/bulk-listing-upload/confirm', body);
};

export const discardBulkListingUpload = body => {
  return post('/api/bulk-listing-upload/discard', body);
};

export const previewBulkListingUploadTitles = body => {
  return post('/api/bulk-listing-upload/title-preview', body);
};

/* P25009-410 — Anslut ditt lager. Uploading fills the catalogue; advertising
 * is this separate choice and takes effect at once. Body: { listingIds (≤ 50),
 * visibility: 'marketplace' | 'catalog' }. */
export const setListingVisibility = body => {
  return post('/api/listing-visibility', body);
};

// The seller's rows for the picker. Body: { jobId?, scope?, page? }.
export const getCatalogueItems = body => {
  return post('/api/catalogue/items', body);
};


// Bygg paket — reach engine (see docs/bygg-paket-reach-motor.md).
export const packageReachPreview = body => {
  return post('/api/package-reach/preview', body);
};

/* Ready packages that fit the car the buyer just looked up (P25009-402 BR-2).
 * Body: { make, model, year?, adType?, limit? }. No auth — a plate lookup does
 * not require an account. */
export const packageSuggestions = body => {
  return post('/api/package-suggestions', body);
};

export const createPackageReachJob = body => {
  return post('/api/package-reach/create', body);
};

export const getPackageReachJobs = body => {
  return post('/api/package-reach/jobs', body);
};

export const getPackageReachJob = body => {
  return post('/api/package-reach/job', body);
};

/* Bulk publish is queued, not synchronous: hundreds of packages take minutes.
 * This returns a jobId; poll getPackageReachJob for progress and failures. */
export const publishPackageReachCandidates = body => {
  return post('/api/package-reach/publish', body);
};

export const getPackageReachModels = body => {
  return post('/api/package-reach/models', body);
};

export const excludePackageReachModel = body => {
  return post('/api/package-reach/exclude-model', body);
};

/* Hjulprogram — a season program instead of hundreds of package ads (P25009-405).
 * See docs/superpowers/specs/wheelplace-hjulprogram-concept-spec.md. */
export const getPackagePrograms = () => {
  return request('/api/package-programs', { method: methods.GET });
};

export const createPackageProgram = body => {
  return post('/api/package-programs', body);
};

export const updatePackageProgram = (id, body) => {
  return request(`/api/package-programs/${id}`, { method: methods.PATCH, body });
};

/* The review step's dry run: which packages the program WOULD publish, priced
 * by their tier, plus how many stay as capacity behind them. Publishes nothing. */
export const previewPackageProgram = (id, body = {}) => {
  return post(`/api/package-programs/${id}/preview`, body);
};

/* Publishing is queued, not synchronous: each package uploads pictures and
 * creates a listing. Returns a jobId; poll getPackageReachJob for progress. */
export const publishPackageProgram = (id, body) => {
  return post(`/api/package-programs/${id}/publish`, body);
};

/* Gap notes for cars the dealer added by hand — "saknar 19-tums 5x108-fälg".
 * The demand suggestions arrive pre-filtered and do not need this. */
export const checkPackageProgramTargets = body => {
  return post('/api/package-programs/check-targets', body);
};

export const switchPackageProgramSeason = body => {
  return post('/api/package-programs/switch-season', body);
};

/* The other packages this dealer published for the same car — in practice
 * their price tiers (P25009-407 AC-3). No auth: a buyer reading a package
 * listing is not logged in as its seller. */
export const getPackageVariants = body => {
  return post('/api/package-variants', body);
};

/* "Vad ska jag köpa in?" — the gap notes grouped by the missing part rather
 * than by car, so a repeated note reads as a buying decision (P25009-407 BR-4). */
export const getPackagePurchasingHints = () => {
  return request('/api/package-programs/purchasing-hints', { method: methods.GET });
};

/* The dealer's real tyres and rims that fit one car, catalogue stock included
 * (P25009-406 AC-3). No auth: a buyer browsing a profile is not the dealer. */
export const getBuildAWheelCatalog = body => {
  return post('/api/build-a-wheel-catalog', body);
};

export const sendContactEmail = body => {
  return post('/api/contact', body);
};

// ---------------------------------------------------------------------------
// Seller finance dashboard (behind REACT_APP_SELLER_FINANCE).
// One client function per /api/seller-finance endpoint. See
// server/api/sellerFinance/* for request/response shapes.
// ---------------------------------------------------------------------------
export const getSellerFinanceSummary = () => post('/api/seller-finance/summary', {});
export const refreshSellerFinance = () => post('/api/seller-finance/refresh', {});
export const getSellerFinanceLedger = body => post('/api/seller-finance/ledger', body || {});
export const getSellerFinancePayouts = body => post('/api/seller-finance/payouts', body || {});
export const getSellerFinancePayoutDetail = payoutId =>
  post(`/api/seller-finance/payouts/${payoutId}`, {});
export const getSellerFinanceCosts = body => post('/api/seller-finance/costs', body || {});
export const getSellerFinanceAuthorizations = () => post('/api/seller-finance/authorizations', {});
export const getSellerFinanceStatement = body => post('/api/seller-finance/statement', body || {});
export const getSellerFinanceSettings = () => post('/api/seller-finance/settings', {});
export const updateSellerFinanceSettings = body =>
  post('/api/seller-finance/settings/update', body || {});

// Listing views (P25009-385). The lifetime publicData.totalViews counter has no
// timestamps, so a weekly figure needs its own time-stamped record.
//
// recordListingView is fire-and-forget: it must never delay or fail a listing
// page. The seller stats endpoint takes no seller argument on purpose — the
// seller comes from the session, so nobody can read a competitor's traffic.
export const recordListingView = body => post('/api/listing-views/record', body || {});
export const getSellerViewStats = () => post('/api/listing-views/seller-stats', {});

// Close (lock) a past month (period = 'YYYY-MM'): freezes its SIE groups and
// stamps a monotonic verifikation sequence. Idempotent.
export const lockSellerFinancePeriod = period =>
  post('/api/seller-finance/lock-period', { period });

// ---------------------------------------------------------------------------
// Self-billing (P25009-435 Track A2).
//
// One status call feeds all three acceptance surfaces — the onboarding
// checkbox, the one-time prompt and the receipt list — so none of them can
// disagree with the server's issuance gate about whether this seller is ready.
// ---------------------------------------------------------------------------
export const getSelfBillingStatus = () => post('/api/seller-finance/self-billing', {});

/**
 * Accept the agreement and, in the same call, fill in the company details a
 * receipt needs. `body` is { accepted: true, via, businessNumber?, adress?,
 * city? }. Sending both together is the point: every business seller today
 * fails the organisation-number check, and asking them twice is not acceptable.
 */
export const acceptSelfBilling = body =>
  post('/api/seller-finance/self-billing/accept', body || {});

export const getSelfBillingReceipts = body =>
  post('/api/seller-finance/self-billing/receipts', body || {});

/**
 * Object to a receipt issued in your name. Records the objection — it does not
 * withdraw the receipt, which has already reached the buyer.
 */
export const objectToSelfBillingReceipt = (receiptNumber, reason) =>
  post(
    `/api/seller-finance/self-billing/receipts/${encodeURIComponent(receiptNumber)}/object`,
    { reason }
  );

/**
 * The operator's side (admin only). Without these an objection is written to a
 * database row and read by nobody, which is a silent failure exactly when the
 * feature starts working.
 *
 * `body` is { status: 'open' | 'resolved' | 'all', limit }.
 */
export const getReceiptObjections = body =>
  post('/api/seller-finance/self-billing/objections', body || {});

/**
 * Email suppressions (P25009-436) — admin only. The addresses mail is no
 * longer sent to (hard bounces, spam complaints), each with the marketplace
 * user behind it so support can phone them. `showAll` also returns the
 * non-blocking rows (soft bounces), which are recorded but still mailed.
 */
export const getEmailSuppressions = ({ showAll = false } = {}) =>
  request(`/api/email-suppressions${showAll ? '?show=all' : ''}`, { method: methods.GET });

/** Clear one address after it is fixed, so mail to it goes out again. */
export const clearEmailSuppression = email =>
  request(`/api/email-suppressions/${encodeURIComponent(email)}`, { method: methods.DELETE });

/** Approved orders whose seller has not been paid yet (admin, P25009-495). */
export const getPayoutQueue = () => request('/api/admin/payouts/queue', { method: methods.GET });

/**
 * Dina annonser's exact lookup (P25009-512): the ids of the signed-in seller's
 * own listings that carry this listing number or reg plate, in every state.
 * → { type: 'tireId'|'plate', listingIds: string[], truncated: boolean }
 */
export const lookupOwnListings = q => post('/api/own-listings/lookup', { q });

/**
 * Support's lookup (admin, P25009-512 BR-9): every listing with this listing
 * number or reg plate, any seller, any state, with the seller's name and email.
 *
 * Both are POSTs on purpose: the text can be a plate, and a query string ends
 * up in the hosting router's request log (BR-13).
 */
export const lookupListingAdmin = q => post('/api/admin/listing-lookup', { q });

/** "Betala ut order X": pay one order through the same code as the daily job.
 *  `confirmHistorical` = the operator checked Stripe for an old order. */
export const payOutOrder = (txId, { confirmHistorical = false } = {}) =>
  post(`/api/admin/payouts/${encodeURIComponent(txId)}/pay`, { confirmHistorical });

/** Mark one objection handled. The note is internal — the seller sees only that
 *  it was handled, and when. */
export const resolveReceiptObjection = (receiptNumber, note) =>
  post(
    `/api/seller-finance/self-billing/objections/${encodeURIComponent(receiptNumber)}/resolve`,
    { note }
  );

// Build a download URL for the ledger CSV export (GET, same-origin cookies).
// `params` may include from/to/categories/status/text and `columns` (array or
// comma string).
export const sellerFinanceLedgerCsvUrl = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    usp.append(k, Array.isArray(v) ? v.join(',') : String(v));
  });
  const qs = usp.toString();
  return `${apiBaseUrl()}/api/seller-finance/export/ledger${qs ? `?${qs}` : ''}`;
};

/**
 * "Bygg ditt hjul": the dealer's supplier catalogue, narrowed to one car
 * (P25009-426). Reads SupplierCatalogItem, not listings — the catalogue holds
 * thousands of articles that deliberately never became listings.
 *
 * `body` is { sellerId, vehicle, diameter, tiretype, kinds, limit }. Public, so
 * it works for a logged-out shopper looking up a plate.
 */
export const getSupplierCatalogueFit = body => post('/api/supplier-catalogue/fit', body || {});

// Build a download URL for the SIE4 export of one month (period = 'YYYY-MM').
export const sellerFinanceSieUrl = period =>
  `${apiBaseUrl()}/api/seller-finance/export/sie?period=${encodeURIComponent(period)}`;

/* -------- Försäljning dashboard (P25009-460) -------- */

/** Summary cards + trend + top-sellers for the /sale dashboard header.
 *  `body` is { preset: '7d'|'30d'|'90d'|'12m'|'custom', from?, to? }. */
export const getSellerSalesSummary = body => post('/api/seller-sales/summary', body || {});

// Download URL for the order-list CSV (GET, same-origin cookies).
// `params` may include from/to (YYYY-MM-DD) and state ('state/…').
export const sellerSalesCsvUrl = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    usp.append(k, String(v));
  });
  const qs = usp.toString();
  return `${apiBaseUrl()}/api/seller-sales/export-csv${qs ? `?${qs}` : ''}`;
};

// ---------------------------------------------------------------------------
// Ekonomi (P25009-496): one row per order, and the order's documents.
// ---------------------------------------------------------------------------
/** The signed-in seller's orders for a period: '2026-10' or '2026'. */
export const getSellerEconomyOrders = period =>
  request(`/api/seller-economy/orders?period=${encodeURIComponent(period)}`, { method: methods.GET });
/** Resend the order confirmation. The body is { to: 'buyer'|'seller'|'other', email? }. */
export const sendOrderConfirmation = (txId, body) =>
  post(`/api/orders/${encodeURIComponent(txId)}/send-confirmation`, body);
/** "Kunden hämtade själv" (P25009-499): stamp an in-person handover on a
 *  shipping order. Fire mark-delivered after it, as after a freight booking. */
export const stampOrderHandover = txId => post(`/api/orders/${encodeURIComponent(txId)}/handed-over`, {});
/** A document PDF, opened in a new tab (GET, same-origin cookies).
 *  The document is 'orderbekraftelse.pdf' or 'utbetalningsverifikation.pdf'. */
export const orderDocumentUrl = (txId, document) =>
  `${apiBaseUrl()}/api/orders/${encodeURIComponent(txId)}/documents/${document}`;

// ---------------------------------------------------------------------------
// Integrationer (P25009-412): the Enterprise business's webhook endpoints and
// delivery log. A secret comes back ONCE, from create and rotate, and never again.
// ---------------------------------------------------------------------------
export const getIntegrations = () => request('/api/integrations', { method: methods.GET });
export const createIntegrationEndpoint = body => post('/api/integrations/endpoints', body);
export const updateIntegrationEndpoint = (id, body) =>
  request(`/api/integrations/endpoints/${encodeURIComponent(id)}`, { method: methods.PATCH, body });
export const deleteIntegrationEndpoint = id =>
  request(`/api/integrations/endpoints/${encodeURIComponent(id)}`, { method: methods.DELETE });
export const rotateIntegrationSecret = id =>
  post(`/api/integrations/endpoints/${encodeURIComponent(id)}/rotate-secret`, {});
export const sendIntegrationTest = id => post(`/api/integrations/endpoints/${encodeURIComponent(id)}/test`, {});
export const getIntegrationDeliveries = (params = {}) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
  return request(`/api/integrations/deliveries${qs ? `?${qs}` : ''}`, { method: methods.GET });
};
export const replayIntegrationDelivery = id =>
  post(`/api/integrations/deliveries/${encodeURIComponent(id)}/replay`, {});
